import { useCallback, useRef } from "react";
import { Ramp } from "../../../../../types";
import { Point, Shape } from "../RampCanvasV2/RampCanvasV2";
import {
  getBoundingBox,
  getTransformHandles,
  getHandleAtPosition,
  scaleFromCorner,
  scaleFromEdge,
  rotateAroundCenter,
  getAngleFromCenter,
  getCornerAnchor,
} from "./math";
import { HandleType, BoundingBox } from "./types";
import { useTransformState } from "./useTransformState";

// Shape-preserving clamp: translates entire shape to keep all points in bounds
// This preserves shape dimensions (unlike per-point clamping which distorts shapes)
function clampShapeToBounds(
  points: Point[],
  canvasWidth: number,
  canvasHeight: number
): Point[] {
  if (points.length === 0) return points;

  // Find how far any point extends outside bounds
  let offsetX = 0;
  let offsetY = 0;

  for (const p of points) {
    // Check left bound - need to move right
    if (p.x < 0 && -p.x > offsetX) {
      offsetX = -p.x;
    }
    // Check right bound - need to move left
    if (p.x > canvasWidth && canvasWidth - p.x < offsetX) {
      offsetX = canvasWidth - p.x;
    }
    // Check top bound - need to move down
    if (p.y < 0 && -p.y > offsetY) {
      offsetY = -p.y;
    }
    // Check bottom bound - need to move up
    if (p.y > canvasHeight && canvasHeight - p.y < offsetY) {
      offsetY = canvasHeight - p.y;
    }
  }

  // No offset needed - all points within bounds
  if (offsetX === 0 && offsetY === 0) {
    return points;
  }

  // Apply offset to all points (preserving shape)
  return points.map((p) => ({
    x: p.x + offsetX,
    y: p.y + offsetY,
  }));
}

interface UseTransformHandlesProps {
  shapes: Shape[];
  setShapes: (shapes: Shape[] | ((prev: Shape[]) => Shape[])) => void;
  activeShapes: string[];
  zoomLevel: number;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  pixelsToFeetRatio: number;
  ramp: Ramp;
}

interface TransformHandlesResult {
  /** Call this in onMouseMove to update hovered handle */
  handleMouseMove: (event: React.MouseEvent, mousePos: Point) => boolean;
  /** Call this in onMouseDown to start transform if on a handle */
  handleMouseDown: (event: React.MouseEvent, mousePos: Point) => boolean;
  /** Call this in onMouseUp to end transform */
  handleMouseUp: () => void;
  /** Whether a transform is currently active */
  isTransforming: boolean;
}

/**
 * Hook for transform handle interactions.
 * Returns handlers that should be called from mouse event handlers.
 * Returns whether the event was handled (consumed by transform logic).
 */
export function useTransformHandles({
  shapes,
  setShapes,
  activeShapes,
  zoomLevel,
  canvasRef,
  pixelsToFeetRatio,
  ramp,
}: UseTransformHandlesProps): TransformHandlesResult {
  // Calculate canvas bounds in pixels
  const canvasWidth = ramp.width * pixelsToFeetRatio;
  const canvasHeight = ramp.depth * pixelsToFeetRatio;
  const {
    activeHandle,
    hoveredHandle,
    startMousePos,
    startBounds,
    startShapePoints,
    startRotationAngle,
    startTransform,
    endTransform,
    setHoveredHandle,
    setStartRotationAngle,
  } = useTransformState();

  // Track whether we're in an active transform
  const isTransforming = activeHandle !== null;

  // Get selected shapes
  const getSelectedShapes = useCallback(() => {
    return shapes.filter((s) => activeShapes.includes(s.id) && !s.locked);
  }, [shapes, activeShapes]);

  // Get bounding box and handles for selected shapes
  const getHandles = useCallback(() => {
    const selectedShapes = getSelectedShapes();
    if (selectedShapes.length === 0) return { bounds: null, handles: [] };

    const bounds = getBoundingBox(selectedShapes);
    if (!bounds) return { bounds: null, handles: [] };
    const handles = getTransformHandles(bounds, zoomLevel);
    return { bounds, handles };
  }, [getSelectedShapes, zoomLevel]);

  /**
   * Handle mouse move - update hovered handle and apply transforms
   */
  const handleMouseMove = useCallback(
    (event: React.MouseEvent, mousePos: Point): boolean => {
      // If we're actively transforming, apply the transform
      if (isTransforming && activeHandle && startMousePos && startBounds) {
        applyTransform(mousePos);
        return true;
      }

      // Otherwise, check for hover
      if (activeShapes.length === 0) {
        if (hoveredHandle !== null) {
          setHoveredHandle(null);
        }
        return false;
      }

      const { handles } = getHandles();
      const handle = getHandleAtPosition(mousePos, handles);
      const newHoveredHandle = handle?.type ?? null;

      if (newHoveredHandle !== hoveredHandle) {
        setHoveredHandle(newHoveredHandle);
      }

      // Return true if hovering a handle (to update cursor)
      return newHoveredHandle !== null;
    },
    [activeShapes, activeHandle, startMousePos, startBounds, hoveredHandle, getHandles, setHoveredHandle, isTransforming]
  );

  /**
   * Apply transform based on active handle type
   */
  const applyTransform = useCallback(
    (currentMousePos: Point) => {
      if (!activeHandle || !startBounds || !startMousePos) return;

      const selectedShapes = getSelectedShapes();
      if (selectedShapes.length === 0) return;

      // Transform each selected shape
      const newShapes = shapes.map((shape) => {
        if (!activeShapes.includes(shape.id) || shape.locked) return shape;

        const originalPoints = startShapePoints.get(shape.id);
        if (!originalPoints) return shape;

        let newPoints: Point[];

        if (activeHandle === "rotate") {
          // Rotation
          const center = { x: startBounds.centerX, y: startBounds.centerY };
          const currentAngle = getAngleFromCenter(center, currentMousePos);
          const startAngle =
            startRotationAngle !== null
              ? startRotationAngle
              : getAngleFromCenter(center, startMousePos);

          if (startRotationAngle === null) {
            setStartRotationAngle(startAngle);
          }

          const angleDelta = currentAngle - startAngle;
          newPoints = rotateAroundCenter(originalPoints, center, angleDelta);
        } else if (activeHandle.startsWith("corner-")) {
          // Corner scaling (proportional)
          const anchor = getCornerAnchor(activeHandle, startBounds);
          newPoints = scaleFromCorner(
            originalPoints,
            anchor,
            startBounds,
            currentMousePos,
            startMousePos,
            true // maintainAspect
          );
        } else if (activeHandle.startsWith("edge-")) {
          // Edge scaling (single axis)
          const edge = activeHandle.replace("edge-", "") as "n" | "e" | "s" | "w";
          newPoints = scaleFromEdge(
            originalPoints,
            edge,
            startBounds,
            currentMousePos,
            startMousePos
          );
        } else {
          return shape;
        }

        // Clamp shape to canvas bounds (preserving dimensions)
        const clampedPoints = clampShapeToBounds(newPoints, canvasWidth, canvasHeight);
        return { ...shape, points: clampedPoints };
      });

      setShapes(newShapes);
    },
    [
      activeHandle,
      startBounds,
      startMousePos,
      shapes,
      activeShapes,
      getSelectedShapes,
      startShapePoints,
      startRotationAngle,
      setStartRotationAngle,
      setShapes,
      canvasWidth,
      canvasHeight,
    ]
  );

  /**
   * Handle mouse down - start transform if clicking a handle
   */
  const handleMouseDown = useCallback(
    (event: React.MouseEvent, mousePos: Point): boolean => {
      if (activeShapes.length === 0) return false;

      const { bounds, handles } = getHandles();
      if (!bounds) return false;

      const handle = getHandleAtPosition(mousePos, handles);
      if (!handle) return false;

      // Start the transform
      const selectedShapes = getSelectedShapes();
      const shapePointsMap = new Map<string, Point[]>();
      selectedShapes.forEach((s) => {
        shapePointsMap.set(s.id, [...s.points]);
      });

      startTransform(handle.type, mousePos, bounds, shapePointsMap);
      return true;
    },
    [activeShapes, getHandles, getSelectedShapes, startTransform]
  );

  /**
   * Handle mouse up - end transform
   */
  const handleMouseUp = useCallback(() => {
    if (isTransforming) {
      endTransform();
    }
  }, [isTransforming, endTransform]);

  return {
    handleMouseMove,
    handleMouseDown,
    handleMouseUp,
    isTransforming,
  };
}
